import type { ApiAdmininsightsusersGetCollectionParams } from '@r/api/generated/client.schemas.ts';
import { useApiAdmininsightsusersGetCollection } from '@r/api/generated/client.ts';
import type { InsightUserInsightUserReadModel } from '@r/api/generated/client.schemas.ts';
import { DataBrickSkeleton } from '@r/components/kpi/skeleton/data-brick-skeleton.tsx';
import { LogIn, PercentCircleIcon, UsersIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getDataBrick } from '@admin/components/data-brick.tsx';
import { useState, useMemo, useCallback } from 'react';
import type { Option } from '@r/components/kpi/filters/select-tag-input.tsx';
import { KpiFilterBar } from '@r/components/kpi/filters/kpi-filter-bar.tsx';
import { companyStatuses } from '@r/types/index.ts';
import type { Role } from '@r/utils/roles.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type UserInsights = InsightUserInsightUserReadModel;

export default function UserInsights() {
  const { t } = useTranslation();

  const [companyStatus, setCompanyStatusFilterValue] = useState<Option[]>([
    { id: 'company.status.registered', name: t('company.status.registered') }
  ]);
  const [companyTypologies, setCompanyTypologiesFilterValue] = useState<Option[]>([]);
  const [selectedSalesIds, setSelectedSalesIds] = useState<string[]>([]);
  const [selectedMaxRoles, setSelectedMaxRoles] = useState<Role[]>(['ROLE_MEMBER', 'ROLE_MANAGER']);
  const [dateRange, setDateRange] = useState<{ from?: string; to?: string }>({});

  const statusIds = companyStatus
    .map((status) => String(status.id))
    .filter((id): id is (typeof companyStatuses)[number] =>
      companyStatuses.includes(id as (typeof companyStatuses)[number])
    );

  const typologyNames = companyTypologies.map((typology) => String(typology.id));
  const usersParams: ApiAdmininsightsusersGetCollectionParams = {
    ...(statusIds.length > 0 ? { 'status[]': statusIds } : {}),
    ...(typologyNames.length > 0 ? { 'typology[]': typologyNames } : {}),
    ...(selectedSalesIds.length > 0 ? { 'sales[]': selectedSalesIds } : {}),
    ...(selectedMaxRoles.length > 0 ? { 'maxRole[]': selectedMaxRoles } : {}),
    ...(dateRange.from ? { from: dateRange.from } : {}),
    ...(dateRange.to ? { to: dateRange.to } : {})
  };
  const userInsightsQuery = useApiAdmininsightsusersGetCollection(usersParams);
  useOrvalQueryErrorToast(userInsightsQuery, t('toast.error.label'));

  function getRegisteredMemberPercentage(adminInsights: UserInsights): number {
    const totalUsers = adminInsights.totalUsers;
    const totalUsersRegistered = adminInsights.totalUsersRegistered;

    if (totalUsers == null || totalUsersRegistered == null || totalUsers === 0) {
      return 0;
    }

    return Math.round((totalUsersRegistered / totalUsers) * 100);
  }

  const initialSelectedStatuses = useMemo(
    () => [{ id: 'company.status.registered', name: t('company.status.registered') }],
    [t]
  );

  const handleDateRangeChange = useCallback((range: { from?: string; to?: string }) => {
    setDateRange(range);
  }, []);

  return (
    <>
      <KpiFilterBar
        onStatusChange={setCompanyStatusFilterValue}
        onTypologyChange={setCompanyTypologiesFilterValue}
        companyTypologies={companyTypologies}
        onSalesChange={setSelectedSalesIds}
        initialSelectedStatuses={initialSelectedStatuses}
        onMaxRoleChange={setSelectedMaxRoles}
        initialSelectedRoles={['ROLE_MEMBER', 'ROLE_MANAGER']}
        onDateRangeChange={handleDateRangeChange}
        dateRange={dateRange}
      />

      <div className="flex flex-wrap space-y-5 grow xl:flex-nowrap xl:space-x-5 xl:space-y-0">
        <div className="grid grid-cols-12 gap-5 w-full h-fit">
          <div className="col-span-12 xl:col-span-4">
            {(userInsightsQuery.isLoading || userInsightsQuery.isError) && (
              <DataBrickSkeleton size={'large'} />
            )}

            {userInsightsQuery.isSuccess &&
              userInsightsQuery.data &&
              userInsightsQuery.data.totalUsers != null && (
                <div className="flex flex-wrap space-x-0 space-y-5 w-full h-full 2xl:flex-nowrap 2xl:space-x-5 2xl:space-y-0">
                  {getDataBrick(
                    t('admin.kpi.total.members'),
                    <UsersIcon />,
                    userInsightsQuery.data.totalUsers,
                    ` ${t('common.members')}`,
                    ` ${t('admin.kpi.total.members.info')}`,
                    null,
                    'large'
                  )}
                </div>
              )}
          </div>

          <div className="col-span-12 xl:col-span-4">
            {(userInsightsQuery.isLoading || userInsightsQuery.isError) && (
              <DataBrickSkeleton size={'large'} />
            )}

            {userInsightsQuery.isSuccess &&
              userInsightsQuery.data &&
              userInsightsQuery.data.totalUsersRegistered != null && (
                <div className="flex flex-wrap space-x-0 space-y-5 w-full h-full 2xl:flex-nowrap 2xl:space-x-5 2xl:space-y-0">
                  {getDataBrick(
                    t('admin.kpi.registered.members.percent'),
                    <PercentCircleIcon />,
                    getRegisteredMemberPercentage(userInsightsQuery.data),
                    ' %',
                    null,
                    null,
                    'large'
                  )}
                </div>
              )}
          </div>

          <div className="col-span-12 xl:col-span-4">
            {(userInsightsQuery.isLoading || userInsightsQuery.isError) && (
              <DataBrickSkeleton size={'large'} />
            )}

            {userInsightsQuery.isSuccess &&
              userInsightsQuery.data &&
              userInsightsQuery.data.totalLogins != null && (
                <div className="flex flex-wrap space-x-0 space-y-5 w-full h-full 2xl:flex-nowrap 2xl:space-x-5 2xl:space-y-0">
                  {getDataBrick(
                    t('admin.kpi.avg.logins'),
                    <LogIn />,
                    userInsightsQuery.data.totalUsers > 0
                      ? Math.round(
                          userInsightsQuery.data.totalLogins / userInsightsQuery.data.totalUsers
                        )
                      : 0,
                    ` ${t('admin.kpi.avg.logins.suffix')}`,
                    t('admin.kpi.avg.logins.info'),
                    null,
                    'large'
                  )}
                </div>
              )}
          </div>
        </div>
      </div>
    </>
  );
}
